import type { UserPreferences } from '../types'

/**
 * IndexedDB cache for user preferences (Track D)。
 *
 * 設計判断:
 *   - サーバ往復しなくても即座に prefs を読めるようにする (network 遅延吸収)
 *   - localStorage でなく IndexedDB を採用: Phase 5 で暗号化済 vault item の
 *     端末キャッシュも IndexedDB に置く予定 (CLAUDE.md §2.6 注記)。Preferences
 *     は同じ store の別 objectStore として共存させやすい
 *   - object store はシンプルに key='current' で 1 件保持 (現在の login user 用)。
 *     logout 時に `clear()` で消す
 *   - 非対応環境 (IndexedDB 無し) は read/write が no-op 相当で動作継続
 */

const DB_NAME = 'ookey'
const DB_VERSION = 1
const STORE_NAME = 'preferences'
const KEY = 'current'

let cachedDb: IDBDatabase | null = null

const openDb = (): Promise<IDBDatabase | null> => {
  // SSR / unit test (jsdom 環境で indexedDB 無し) は null を返して no-op
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)

  if (cachedDb !== null) return Promise.resolve(cachedDb)

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)

    req.onupgradeneeded = (): void => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
    req.onsuccess = (): void => {
      cachedDb = req.result
      resolve(cachedDb)
    }
    req.onerror = (): void => reject(req.error)
  })
}

/** 暗号化なしのまま IndexedDB に書く (非機密データ前提)。 */
export const writeCachedPreferences = async (prefs: UserPreferences): Promise<void> => {
  const db = await openDb()
  if (!db) return

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)

    store.put(prefs, KEY)
    tx.oncomplete = (): void => resolve()
    tx.onerror = (): void => reject(tx.error)
  })
}

/** 取得失敗 / 未保存 / 非対応環境は `null` を返す。 */
export const readCachedPreferences = async (): Promise<UserPreferences | null> => {
  const db = await openDb()
  if (!db) return null

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const req = store.get(KEY)

    req.onsuccess = (): void => {
      const result = req.result as UserPreferences | undefined

      resolve(result ?? null)
    }
    req.onerror = (): void => reject(req.error)
  })
}

/** ログアウト時のクリーンアップ。 */
export const clearCachedPreferences = async (): Promise<void> => {
  const db = await openDb()
  if (!db) return

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)

    store.delete(KEY)
    tx.oncomplete = (): void => resolve()
    tx.onerror = (): void => reject(tx.error)
  })
}

/** spec 用: cached DB 接続をリセット (テスト間隔離)。 */
export const __resetCachedDbForTest = (): void => {
  cachedDb?.close()
  cachedDb = null
}
